import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  MousePointerClick,
  Eye,
  CheckCircle2,
  Pause,
  Play,
  Users,
  Bot,
  Smartphone,
  Loader2,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { advertiserApi, AdDetails } from '../../api';
import { useTelegram } from '../../hooks';

interface AdsListProps {
  onCreateAd: () => void;
  onRefresh: () => void;
}

const AD_TYPE_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  telegram_channel: { icon: Users, label: 'Channel', color: 'bg-blue-500' },
  telegram_group: { icon: Users, label: 'Group', color: 'bg-green-500' },
  telegram_bot: { icon: Bot, label: 'Bot', color: 'bg-purple-500' },
  telegram_miniapp: { icon: Smartphone, label: 'Mini App', color: 'bg-orange-500' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  active: { label: 'Active', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  paused: { label: 'Paused', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  pending: { label: 'Pending', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  completed: { label: 'Completed', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
  rejected: { label: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  expired: { label: 'Expired', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
};

export function AdsList({ onCreateAd, onRefresh }: AdsListProps) {
  const [ads, setAds] = useState<AdDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAd, setSelectedAd] = useState<AdDetails | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { hapticFeedback, hapticNotification } = useTelegram();

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await advertiserApi.getAds();
      
      if (response.success && response.ads) {
        setAds(response.ads);
        setError(null);
      } else {
        setError('Failed to load ads');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (adId: string) => {
    hapticFeedback('medium');
    setActionLoading(adId);

    try {
      const response = await advertiserApi.pauseAd(adId);
      if (response.success) {
        hapticNotification('success');
        fetchAds();
        onRefresh();
      } else {
        hapticNotification('error');
      }
    } catch (err) {
      hapticNotification('error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (adId: string) => {
    hapticFeedback('medium');
    setActionLoading(adId);

    try {
      const response = await advertiserApi.resumeAd(adId);
      if (response.success) {
        hapticNotification('success');
        fetchAds();
        onRefresh();
      } else {
        hapticNotification('error');
      }
    } catch (err) {
      hapticNotification('error');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-white/60">Loading ads...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">{error}</p>
            <button
              onClick={fetchAds}
              className="text-sm text-red-300 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16"
      >
        <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
          <MousePointerClick className="w-10 h-10 text-white/40" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">No Ads Yet</h3>
        <p className="text-white/60 mb-6">Create your first ad campaign to get started</p>
        <button
          onClick={onCreateAd}
          className="px-6 py-3 rounded-xl bg-primary text-white font-semibold flex items-center gap-2 mx-auto"
        >
          <Plus className="w-5 h-5" />
          Create Ad
        </button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Create New Button */}
      <button
        onClick={onCreateAd}
        className="w-full py-3 rounded-xl bg-primary/20 border border-primary/30 text-primary font-medium flex items-center justify-center gap-2 hover:bg-primary/30 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Create New Ad
      </button>

      {/* Ads List */}
      {ads.map((ad, index) => {
        const typeConfig = AD_TYPE_CONFIG[ad.type] || AD_TYPE_CONFIG.website;
        const statusConfig = STATUS_CONFIG[ad.status] || STATUS_CONFIG.pending;
        const Icon = typeConfig.icon;

        return (
          <motion.div
            key={ad.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-surface rounded-xl border border-white/10 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white truncate">{ad.title}</h3>
                      <p className="text-sm text-white/60 line-clamp-1">{ad.description}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-1 text-xs text-white/50">
                      <Eye className="w-3 h-3" />
                      {ad.stats.impressions.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-white/50">
                      <MousePointerClick className="w-3 h-3" />
                      {ad.stats.clicks.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-white/50">
                      <CheckCircle2 className="w-3 h-3" />
                      {ad.stats.completions.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-primary">
                      {ad.completedCount}/{ad.targetCompletions}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${Math.min(100, (ad.completedCount / ad.targetCompletions) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
                {ad.status === 'active' && (
                  <button
                    onClick={() => handlePause(ad.id)}
                    disabled={actionLoading === ad.id}
                    className="flex-1 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {actionLoading === ad.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Pause className="w-4 h-4" />
                        Pause
                      </>
                    )}
                  </button>
                )}
                {ad.status === 'paused' && (
                  <button
                    onClick={() => handleResume(ad.id)}
                    disabled={actionLoading === ad.id}
                    className="flex-1 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {actionLoading === ad.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Resume
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setSelectedAd(ad)}
                  className="flex-1 py-2 rounded-lg bg-white/10 text-white/70 text-sm font-medium flex items-center justify-center gap-1.5"
                >
                  View Details
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Ad Details Modal */}
      <AdDetailsModal
        ad={selectedAd}
        onClose={() => setSelectedAd(null)}
      />
    </div>
  );
}

// Ad Details Modal Component
function AdDetailsModal({ ad, onClose }: { ad: AdDetails | null; onClose: () => void }) {
  if (!ad) return null;

  const typeConfig = AD_TYPE_CONFIG[ad.type] || AD_TYPE_CONFIG.website;
  const statusConfig = STATUS_CONFIG[ad.status] || STATUS_CONFIG.pending;
  const Icon = typeConfig.icon;

  return (
    <AnimatePresence>
      {ad && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto"
          >
            <div className="bg-surface rounded-t-3xl p-6">
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-14 h-14 rounded-xl ${typeConfig.color} flex items-center justify-center`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white">{ad.title}</h2>
                  <p className="text-white/60">{ad.description}</p>
                  <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50 mb-1">Impressions</p>
                  <p className="text-lg font-bold text-white">{ad.stats.impressions.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50 mb-1">Clicks</p>
                  <p className="text-lg font-bold text-white">{ad.stats.clicks.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50 mb-1">Completions</p>
                  <p className="text-lg font-bold text-white">{ad.stats.completions.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50 mb-1">Total Spent</p>
                  <p className="text-lg font-bold text-white">{ad.stats.spent.toLocaleString()}</p>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/60">Progress</span>
                  <span className="text-sm font-medium text-white">
                    {ad.completedCount} / {ad.targetCompletions}
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(100, (ad.completedCount / ad.targetCompletions) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-white/50 mt-2">
                  {Math.round((ad.completedCount / ad.targetCompletions) * 100)}% complete
                </p>
              </div>

              {/* Config Details */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h3 className="font-medium text-white mb-3">Configuration</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Reward per completion</span>
                    <span className="text-white">{ad.rewardPerCompletion} points</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Verification time</span>
                    <span className="text-white">{ad.verificationTime}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Target completions</span>
                    <span className="text-white">{ad.targetCompletions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Budget remaining</span>
                    <span className="text-white">{ad.budgetRemaining.toLocaleString()} points</span>
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full py-4 rounded-xl bg-white/10 text-white font-medium"
              >
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
